import React, { useState, useRef } from 'react';
import classNames from 'classnames';

import Tooltip from '../Tooltip/Tooltip';
import Gutter from '../Gutter/Gutter';
import AmountChip from './AmountChip/AmountChip';
import BaseInput, { BaseInputPropsWithChildren } from '../Input/BaseInput';
import OptionList, { OptionListOption } from '../OptionList/OptionList';
import BodyText from '../Typography/BodyText/BodyText';

import isIE11 from '../../shared/utils/isIE11/isIE11';
import { CreateProps } from '../../types/utils/CreateProps';

import styles from './DropdownMultiselect.module.scss';
import ResolveIcon from '../Input/ResolveIcon';

type DropdownMultiselectProps = CreateProps<
  {
    /** Label shown on the dropdown */
    label?: string;
    /** List of options */
    options: OptionListOption[];
    /** Tooltip shown when hover clear button  */
    tooltip?: string;
    /** OnChange handler when user makes selection. Return value is the selected option as an object and native event */
    onChange: (
      event: React.SyntheticEvent<HTMLDivElement>,
      option: OptionListOption
    ) => void;
    /** onClear handler when user clears selection */
    onClear: () => void;
    /** Placeholder is visible when the input is empty */
    placeholder?: string;
    /** Amountchip or placeholder which will be visible in the input */
    children?: React.ReactElement | React.ReactNodeArray;
  },
  BaseInputPropsWithChildren,
  | 'children'
  | 'wrapperTabIndex'
  | 'inputTabIndex'
  | 'confirmed'
  | 'readonly'
  | 'onClick'
  | 'ref'
  | 'borderRadius'
>;

const DropdownMultiselect: React.FunctionComponent<DropdownMultiselectProps> = ({
  options,
  onChange,
  onClear,
  errorText,
  disabled,
  className,
  placeholder,
  tooltip,
  ...props
}) => {
  const [open, setOpen] = useState(false);
  const [focusedOptionId, setFocusedOptionId] = useState<string>();

  const inputElement = useRef<HTMLInputElement>(null);
  const dropdownElement = useRef<HTMLDivElement>(null);

  const focusOnFirstFocusableOption = () => {
    const firstFocusableOption = options.filter(
      (option) => !option.disabled
    )[0];

    setFocusedOptionId(firstFocusableOption.value);
  };

  const handleInputClick = () => {
    setOpen(!open);
  };

  const handleOptionListBlur = (
    event:
      | React.FocusEvent<HTMLDivElement>
      | React.KeyboardEvent<HTMLDivElement>
  ) => {
    const isFocusEvent = (
      e: typeof event
    ): e is React.FocusEvent<HTMLDivElement> => 'relatedTarget' in e;

    /* This logic is related to the keyboard usability (closing on ESC) which is not supported in IE. That's why we leave IE out  */
    /* istanbul ignore else */
    if (!isIE11) {
      if (
        !isFocusEvent(event) ||
        !dropdownElement.current?.contains(event.relatedTarget as HTMLElement)
      ) {
        setOpen(false);
      }
    }
  };

  const handleOnBlur = (
    event:
      | React.FocusEvent<HTMLDivElement>
      | React.KeyboardEvent<HTMLDivElement>
  ) => {
    const isFocusEvent = (
      e: typeof event
    ): e is React.FocusEvent<HTMLDivElement> => 'relatedTarget' in e;

    /* istanbul ignore else */
    if (!isIE11) {
      /* istanbul ignore else  */
      if (
        !isFocusEvent(event) ||
        (!dropdownElement.current?.contains(
          event.relatedTarget as HTMLElement
        ) &&
          open)
      ) {
        setOpen(false);
      }
    }

    /* istanbul ignore next  */
    if (isIE11 && typeof document !== 'undefined') {
      /* Prevent the onBlur in IE if user has clicked an option from the list or the scrollbar inside the optionlist */
      if (
        document.activeElement &&
        document.activeElement.className.indexOf('telia-react-optionlist') < 0
      ) {
        setOpen(false);
      }
    }
  };

  const handleOptionSelect = (
    event: React.SyntheticEvent<HTMLDivElement>,
    option: OptionListOption
  ) => {
    onChange(event, option);
  };

  const handleOnClear = (event: React.SyntheticEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onClear();
  };

  const keyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      handleInputClick();
    }

    if (
      (event.key === 'ArrowDown' && !open) ||
      (event.key === 'ArrowUp' && !open)
    ) {
      event.preventDefault();
      focusOnFirstFocusableOption();
      setOpen(true);
    }
    if (event.key === 'Tab') {
      setFocusedOptionId('');
    }
  };

  const selectedOptionAmount = options.filter(
    (option) => option.checked === true
  ).length;

  const amountChip = (
    <AmountChip
      disabled={disabled}
      text={selectedOptionAmount}
      onClick={disabled ? undefined : handleOnClear}
      tabIndex={disabled ? -1 : 0}
    />
  );

  return (
    <div
      className={classNames([
        styles.dropdownMultiselect,
        { [styles.dropdownMultiselect_disabled]: disabled },
        { [styles.dropdownMultiselect_error]: errorText },
        { [styles.dropdownMultiselect_open]: open },
        className,
      ])}
      ref={dropdownElement}
      onBlur={handleOnBlur}
    >
      <div
        className={styles.dropdownMultiselect__container}
        tabIndex={0}
        onKeyDown={keyDown}
      >
        <BaseInput
          {...props}
          disabled={disabled}
          wrapperTabIndex={-1}
          confirmed={false}
          onClick={disabled ? undefined : handleInputClick}
          ref={inputElement}
          errorText={errorText && errorText === ' ' ? ' ' : errorText}
          borderRadius={{ size: 'sm' }}
        >
          {!!selectedOptionAmount && (
            <>
              {!!tooltip && !disabled ? (
                <Tooltip
                  placement="bottomStart"
                  tooltipContent={tooltip}
                  trigger="hover"
                >
                  {amountChip}
                </Tooltip>
              ) : (
                <>{amountChip}</>
              )}

              <Gutter size="xs" />
            </>
          )}

          <BodyText color="gray400" tagName="span" flexItem singleLine>
            {placeholder}
          </BodyText>
          <Gutter size="xs" />
          <ResolveIcon
            errorText={errorText}
            iconColor={open ? 'purple500' : 'black'}
            iconName={open ? 'chevron-up' : 'chevron-down'}
            disabled={disabled}
          />
        </BaseInput>
      </div>
      {!!open && (
        <OptionList
          options={options}
          onBlur={handleOptionListBlur}
          optionOnClick={handleOptionSelect}
          focusedOptionId={focusedOptionId}
        />
      )}
    </div>
  );
};

export default DropdownMultiselect;
